import { useCallback, useMemo, useState } from 'react';

import { MAX_FILE_BYTES } from '../../constants/externalDocuments';
import type { IQuote, IQuoteFileInput } from '../../interfaces/quotes';
import { Quotes } from '../../services/api/quotes/quotes';
import { TRANSLATIONS_EXTERNAL_BENEFITS } from '../../translations/commons';
import { QuoteDocumentType } from '../../types/quotes';
import { buildQuoteUpdateBody } from '../../utils/quotes/quotes';

export interface StagedFile {
  id: string;
  file: File;
}

type StagedByType = Record<QuoteDocumentType, StagedFile[]>;

const emptyStaged = (): StagedByType =>
  Object.fromEntries(Object.values(QuoteDocumentType).map((type) => [type, [] as StagedFile[]])) as StagedByType;

const MULTI_TYPES = new Set<QuoteDocumentType>([QuoteDocumentType.Sbc, QuoteDocumentType.Other]);

interface UseExternalBenefitsSubmitParams {
  brokerId: string;
  prospectId: number;
  quote: IQuote;
  onSubmitted: (updated: IQuote) => void;
}

export interface UseExternalBenefitsSubmit {
  staged: StagedByType;
  removals: Set<number>;
  errors: Partial<Record<QuoteDocumentType, string>>;
  submitting: boolean;
  failed: boolean;
  submitted: boolean;
  hasChanges: boolean;
  hasRemovals: boolean;
  addFiles: (documentType: QuoteDocumentType, files: File[]) => void;
  removeStaged: (documentType: QuoteDocumentType, stagedId: string) => void;
  removeSent: (fileId: number) => void;
  submit: () => Promise<void>;
  reset: () => void;
}

const oversizeError = TRANSLATIONS_EXTERNAL_BENEFITS.slot.tooLarge;

export const useExternalBenefitsSubmit = ({
  brokerId,
  prospectId,
  quote,
  onSubmitted
}: UseExternalBenefitsSubmitParams): UseExternalBenefitsSubmit => {
  const [staged, setStaged] = useState<StagedByType>(emptyStaged);
  const [removals, setRemovals] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<Partial<Record<QuoteDocumentType, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedIds, setUploadedIds] = useState<Set<string>>(new Set());
  const [deletesApplied, setDeletesApplied] = useState(false);

  const stagedCount = useMemo(() => Object.values(staged).reduce((total, list) => total + list.length, 0), [staged]);

  const hasRemovals = removals.size > 0;
  const hasChanges = stagedCount > 0 || hasRemovals;

  const addFiles = useCallback((documentType: QuoteDocumentType, files: File[]) => {
    const withinSize = files.filter((file) => file.size <= MAX_FILE_BYTES);
    const rejected = files.length - withinSize.length;

    setErrors((prev) => {
      const next = { ...prev };
      if (rejected > 0) next[documentType] = oversizeError;
      else delete next[documentType];
      return next;
    });

    if (withinSize.length === 0) return;

    const stampBase = Date.now();
    const staging = withinSize.map((file, index) => ({
      id: `${documentType}-${stampBase}-${index}-${file.name}`,
      file
    }));

    setStaged((prev) => ({
      ...prev,
      [documentType]: MULTI_TYPES.has(documentType) ? [...prev[documentType], ...staging] : staging
    }));
  }, []);

  const removeStaged = useCallback((documentType: QuoteDocumentType, stagedId: string) => {
    setStaged((prev) => ({
      ...prev,
      [documentType]: prev[documentType].filter((entry) => entry.id !== stagedId)
    }));
  }, []);

  const removeSent = useCallback((fileId: number) => {
    setRemovals((prev) => new Set(prev).add(fileId));
  }, []);

  const reset = useCallback(() => {
    setStaged(emptyStaged());
    setRemovals(new Set());
    setErrors({});
    setSubmitting(false);
    setFailed(false);
    setSubmitted(false);
    setUploadedIds(new Set());
    setDeletesApplied(false);
  }, []);

  const submit = useCallback(async () => {
    if (!hasChanges) return;

    setSubmitting(true);
    setFailed(false);

    try {
      if (hasRemovals && !deletesApplied) {
        const files: IQuoteFileInput[] = quote.files
          .filter((file) => !removals.has(file.id))
          .map((file) => ({ id: file.id, fileJson: file.fileJson }));
        await Quotes.updateQuote(brokerId, prospectId, quote.id, buildQuoteUpdateBody(quote, { files }));
        setDeletesApplied(true);
      }

      const pending = Object.entries(staged).flatMap(([documentType, list]) =>
        list
          .filter((entry) => !uploadedIds.has(entry.id))
          .map((entry) => ({ documentType: documentType as QuoteDocumentType, entry }))
      );

      const uploadedNow = await Promise.all(
        pending.map(({ documentType, entry }) =>
          Quotes.uploadQuoteFile(brokerId, prospectId, quote.id, entry.file, documentType).then(() => entry.id)
        )
      );

      if (uploadedNow.length > 0) {
        setUploadedIds((prev) => {
          const next = new Set(prev);
          uploadedNow.forEach((id) => next.add(id));
          return next;
        });
      }

      const quotes = await Quotes.getQuotes(brokerId, prospectId);
      const fresh = quotes.find((entry) => entry.id === quote.id);
      if (fresh) onSubmitted(fresh);

      setSubmitted(true);
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  }, [
    brokerId,
    prospectId,
    quote,
    staged,
    removals,
    uploadedIds,
    deletesApplied,
    hasChanges,
    hasRemovals,
    onSubmitted
  ]);

  return {
    staged,
    removals,
    errors,
    submitting,
    failed,
    submitted,
    hasChanges,
    hasRemovals,
    addFiles,
    removeStaged,
    removeSent,
    submit,
    reset
  };
};
